import React from "react";
import { motion } from "framer-motion";
import { useContent } from "../lib/useContent";
import { mediaSrc } from "../lib/media";

const timeline = [
    { year: "2026", event: "Forthcoming solo exhibition — The Boys Behind the Doors, London" },
    { year: "2025", event: "Budapest Residency — Magyar Műhely Studio" },
    { year: "2024", event: "Group Exhibition — Form & Residue, Vienna" },
    { year: "2023", event: "First Watercolour Heads series exhibited, Berlin" },
    { year: "2022", event: "Mixed Media Heads — Private Collections, Paris" },
    { year: "2021", event: "Begins Still Life Drawings, ongoing series" },
    { year: "2020", event: "MFA, Royal College of Art, London" },
];

export default function About() {
    const { about } = useContent();
    return (
        <div data-testid="about-page" className="pt-32 md:pt-40">
            <section className="px-6 md:px-12 lg:px-24">
                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-7 order-2 lg:order-1">
                        <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-accent">
                            About the Artist
                        </p>
                        <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-aplum mt-4">
                            Alexandra <em className="italic">Plum</em>.
                        </h1>
                        <p className="font-serif-display text-xl md:text-2xl text-aplum-dim mt-8 leading-relaxed italic">
                            “To draw is to attend. To paint is to surrender. I move between the two
                            as a way of listening to what the figure refuses to say aloud.”
                        </p>

                        <div className="mt-16 space-y-8 font-sans-display text-base text-aplum-dim leading-relaxed max-w-2xl">
                            {about.paragraphs.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:col-span-5 order-1 lg:order-2"
                    >
                        <div className="sticky top-32">
                            <img
                                src={mediaSrc(about.portrait)}
                                alt={about.portrait?.alt ?? ''}
                                data-testid="about-portrait"
                                className="w-full h-[60vh] lg:h-[80vh] object-cover"
                            />
                            <p className="font-sans-display text-[11px] uppercase tracking-widest-x text-aplum-dim mt-4">
                                Portrait — Studio, London 2025
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* CV / Timeline */}
            <section className="px-6 md:px-12 lg:px-24 mt-40">
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
                        <div className="md:col-span-3">
                            <p className="font-sans-display text-[10px] uppercase tracking-widest-x text-aplum-accent">
                                02 — CV
                            </p>
                        </div>
                        <div className="md:col-span-9">
                            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tighter text-aplum">
                                Selected Exhibitions & Achievements
                            </h2>
                        </div>
                    </div>

                    <div className="border-t border-aplum" data-testid="cv-timeline">
                        {timeline.map((row, i) => (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.6, delay: i * 0.05 }}
                                className="grid grid-cols-12 gap-6 py-8 border-b border-aplum hover:bg-aplum-surface/40 transition-colors duration-500 px-2"
                            >
                                <div className="col-span-3 md:col-span-2 font-sans-display text-xs uppercase tracking-widest-x text-aplum-accent pt-1">
                                    {row.year}
                                </div>
                                <div className="col-span-9 md:col-span-10 font-serif-display text-xl md:text-2xl text-aplum tracking-tight">
                                    {row.event}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
